/**
 * Browser-derived device properties for the demo's Device Info modal.
 *
 * Production-on-device gets richer data via the SOTI JS Agent API (asset
 * tag, IMEI, real signal dBm, VPN state, SSID). Until that AgentHost
 * capability lands (Phase 4.5+), this module surfaces what the WebView
 * itself knows, which is more useful for support than hardcoded
 * placeholders alone.
 *
 * Everything here gracefully degrades — every field is either populated
 * from a stable API or marked optional/`null`. Nothing throws.
 */

export interface BrowserDeviceProperties {
  userAgent: string;
  /** Short OS name parsed from UA. e.g., "Android 14", "macOS", "iOS 17". */
  os: string;
  /** Short browser name + version. e.g., "Chrome 131", "Safari". */
  browser: string;
  /** Best-effort tablet/phone/desktop classification from UA. */
  formFactor: 'phone' | 'tablet' | 'desktop' | 'unknown';
  /** Resolved BCP-47 locale, e.g., "en-US". */
  language: string;
  /** IANA time zone identifier, e.g., "America/Los_Angeles". */
  timezone: string;
  /** Screen pixel dimensions + DPR. */
  screen: { width: number; height: number; pixelRatio: number };
  /** `navigator.onLine` snapshot. */
  online: boolean;

  // Maybe-available (Chromium-leaning).
  hardwareConcurrency?: number;
  /** Reported in GB; rounded to 0.1. */
  deviceMemoryGb?: number;
  connection?: {
    /** wifi · cellular · ethernet · etc. (if browser exposes it). */
    type?: string;
    /** 4g · 3g · 2g · slow-2g — Network Information API estimate. */
    effectiveType?: string;
    /** Mbps estimate. */
    downlinkMbps?: number;
    /** Round-trip estimate in ms. */
    rttMs?: number;
  };
}

export interface BrowserStorageInfo {
  /** Bytes the origin is currently using. */
  usageBytes: number;
  /** Bytes the origin is allowed to use. */
  quotaBytes: number;
}

/**
 * Synchronously collect every property that's available without an `await`.
 * Storage info is async — fetch separately via {@link getStorageInfo}.
 */
export function collectDeviceProperties(): BrowserDeviceProperties {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const language =
    (typeof navigator !== 'undefined'
      ? navigator.language || navigator.languages?.[0]
      : undefined) ?? 'en';
  const timezone = safeTimezone();
  const screenInfo = safeScreen();
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;

  const props: BrowserDeviceProperties = {
    userAgent: ua,
    os: parseOs(ua),
    browser: parseBrowser(ua),
    formFactor: parseFormFactor(ua),
    language,
    timezone,
    screen: screenInfo,
    online,
  };

  if (typeof navigator !== 'undefined') {
    const concurrency = navigator.hardwareConcurrency;
    if (typeof concurrency === 'number') props.hardwareConcurrency = concurrency;

    const mem = (navigator as { deviceMemory?: number }).deviceMemory;
    if (typeof mem === 'number') props.deviceMemoryGb = mem;

    const c = (navigator as { connection?: NetworkConnection }).connection;
    if (c) {
      const conn: NonNullable<BrowserDeviceProperties['connection']> = {};
      if (typeof c.type === 'string') conn.type = c.type;
      if (typeof c.effectiveType === 'string') conn.effectiveType = c.effectiveType;
      if (typeof c.downlink === 'number') conn.downlinkMbps = c.downlink;
      if (typeof c.rtt === 'number') conn.rttMs = c.rtt;
      props.connection = conn;
    }
  }

  return props;
}

/**
 * Async storage usage/quota via `navigator.storage.estimate`. Returns
 * `null` if the API isn't available or the call rejects.
 */
export async function getStorageInfo(): Promise<BrowserStorageInfo | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  try {
    const est = await navigator.storage.estimate();
    if (typeof est.usage !== 'number' || typeof est.quota !== 'number') return null;
    return { usageBytes: est.usage, quotaBytes: est.quota };
  } catch {
    return null;
  }
}

/** Format bytes as a friendly "MB" or "GB" string. */
export function formatBytes(bytes: number): string {
  const gb = bytes / 1_000_000_000;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / 1_000_000;
  return `${mb.toFixed(0)} MB`;
}

/* ───────────── Internal: lightweight UA parsing ───────────── */

interface NetworkConnection {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

function parseOs(ua: string): string {
  // Order matters — Android UA includes "Linux" too, so check Android first.
  if (/Android\s([\d.]+)/i.test(ua)) {
    const v = /Android\s([\d.]+)/i.exec(ua)?.[1];
    return `Android ${v ?? ''}`.trim();
  }
  if (/(iPad|iPhone|iPod);[^)]*OS\s(\d+_\d+)/i.test(ua)) {
    const v = /OS\s(\d+_\d+)/i.exec(ua)?.[1]?.replace('_', '.');
    return `iOS ${v ?? ''}`.trim();
  }
  if (/Mac OS X\s(\d+_\d+)/i.test(ua)) {
    const v = /Mac OS X\s(\d+_\d+)/i.exec(ua)?.[1]?.replace('_', '.');
    return `macOS ${v ?? ''}`.trim();
  }
  if (/Windows NT\s([\d.]+)/i.test(ua)) {
    const v = /Windows NT\s([\d.]+)/i.exec(ua)?.[1];
    return `Windows NT ${v ?? ''}`.trim();
  }
  if (/CrOS\s/i.test(ua)) return 'ChromeOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown OS';
}

function parseBrowser(ua: string): string {
  if (/Edg\/(\d+)/i.test(ua)) return `Edge ${/Edg\/(\d+)/i.exec(ua)?.[1] ?? ''}`.trim();
  if (/Chrome\/(\d+)/i.test(ua) && !/Edg/i.test(ua)) {
    return `Chrome ${/Chrome\/(\d+)/i.exec(ua)?.[1] ?? ''}`.trim();
  }
  if (/Firefox\/(\d+)/i.test(ua)) return `Firefox ${/Firefox\/(\d+)/i.exec(ua)?.[1] ?? ''}`.trim();
  if (/Version\/(\d+).*Safari/i.test(ua)) {
    return `Safari ${/Version\/(\d+)/i.exec(ua)?.[1] ?? ''}`.trim();
  }
  return 'Unknown browser';
}

function parseFormFactor(ua: string): BrowserDeviceProperties['formFactor'] {
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobile|iPhone|Android.*Mobile/i.test(ua)) return 'phone';
  if (/Android/i.test(ua)) return 'tablet'; // Android, not Mobile → tablet
  if (/Macintosh|Windows|Linux/i.test(ua)) return 'desktop';
  return 'unknown';
}

function safeTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function safeScreen(): BrowserDeviceProperties['screen'] {
  if (typeof window === 'undefined') return { width: 0, height: 0, pixelRatio: 1 };
  return {
    width: window.screen.width,
    height: window.screen.height,
    pixelRatio: window.devicePixelRatio || 1,
  };
}
